import { useState } from "react"
import { Navigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Building2, UserPlus, Pencil, Trash2 } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { SettingsTabs } from "@/app/components/SettingsTabs"
import { SettingsCard } from "@/app/components/SettingsCard"
import { Modal } from "@/app/components/Modal"
import { UserAvatar } from "@/app/components/UserAvatar"
import { SelectMenu } from "@/app/components/SelectMenu"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import type { Role } from "@/app/hooks/useCurrentUser"
import type { ClientTheme } from "@/app/lib/themes"
import {
  ErrorBanner,
  SuccessBanner,
  inputStyle,
  labelStyle,
  formatApiError,
} from "@/app/pages/UnitsPage/UnitsPage"
import { primaryButton, secondaryButton } from "@/app/lib/buttonStyles"

interface ClientUser {
  id: string
  name: string | null
  role: Role
  email_address: string
  avatar_url: string | null
}

const ROLE_OPTIONS: { label: string; value: Role }[] = [
  { label: "Owner", value: "owner" },
  { label: "Editor", value: "editor" },
  { label: "Viewer", value: "viewer" },
]

const ROLE_CAPTIONS: Record<Role, string> = {
  owner: "Full access, can manage users",
  editor: "Can add and edit records",
  viewer: "Read-only",
}

// "owner" -> "Owner". Falls back to the raw value for any unexpected role.
function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1)
}

const blankForm = { email: "", name: "", role: "viewer" as Role }
type FormState = typeof blankForm

export function CompanySettingsPage() {
  const { theme } = useClientTheme()
  const { user, canManageUsers, isLoading } = useCurrentUser()
  const queryClient = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(blankForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  // The email we invited, so the success banner can name it after the form closes.
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null)

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ClientUser[]>("/users"),
    enabled: canManageUsers,
  })

  const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: ["users"] })

  const createUser = useMutation({
    mutationFn: (payload: { email_address: string; name: string; role: Role }) =>
      api.post("/users", { user: payload }),
    onSuccess: (_data, payload) => {
      invalidateUsers()
      setInvitedEmail(payload.email_address)
      resetForm()
    },
  })

  const updateUser = useMutation({
    mutationFn: ({ id, name, role }: { id: string; name: string; role: Role }) =>
      api.patch(`/users/${id}`, { user: { name, role } }),
    onSuccess: () => {
      invalidateUsers()
      resetForm()
    },
  })

  const deleteUser = useMutation({
    mutationFn: (id: string) => api.del(`/users/${id}`),
    onSuccess: invalidateUsers,
  })

  // Non-managers never see this page — the dropdown hides the link, but guard the
  // route directly too so a hand-typed URL redirects rather than 403-ing the API.
  if (!isLoading && !canManageUsers) {
    return <Navigate to="/inventory" replace />
  }

  if (isLoading || !user) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[760px] mx-auto"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  const list = users ?? []
  const editing = editingId !== null

  function resetForm() {
    setForm(blankForm)
    setEditingId(null)
    setShowForm(false)
    setValidationError(null)
  }

  function startAdd() {
    createUser.reset()
    updateUser.reset()
    setInvitedEmail(null)
    setForm(blankForm)
    setEditingId(null)
    setValidationError(null)
    setShowForm(true)
  }

  function startEdit(u: ClientUser) {
    createUser.reset()
    updateUser.reset()
    setInvitedEmail(null)
    setForm({ email: u.email_address, name: u.name ?? "", role: u.role })
    setEditingId(u.id)
    setValidationError(null)
    setShowForm(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setValidationError(null)
    createUser.reset()
    updateUser.reset()

    if (editingId) {
      updateUser.mutate({ id: editingId, name: form.name.trim(), role: form.role })
      return
    }

    if (!form.email.trim()) {
      setValidationError("Email is required.")
      return
    }
    createUser.mutate({
      email_address: form.email.trim(),
      name: form.name.trim(),
      role: form.role,
    })
  }

  function handleRemove(u: ClientUser) {
    deleteUser.reset()
    // Clear any lingering "Invite sent" banner so a delete 422 doesn't surface
    // alongside a stale green success message.
    setInvitedEmail(null)
    createUser.reset()
    // window.confirm is deliberate here (not the inline two-click pattern used
    // elsewhere): removing a user revokes access and destroys their sessions, so
    // the hard stop plus the "signed out immediately" warning is the right
    // friction — and that warning is exactly what an inline confirm can't carry.
    const confirmed = window.confirm(
      `Remove ${u.name || u.email_address}? They will be signed out immediately.`
    )
    if (confirmed) deleteUser.mutate(u.id)
  }

  const formError =
    validationError ??
    (editing
      ? updateUser.isError
        ? formatApiError(updateUser.error)
        : null
      : createUser.isError
        ? formatApiError(createUser.error)
        : null)
  const formSaving = editing ? updateUser.isPending : createUser.isPending

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[760px] mx-auto">
        <SettingsTabs />

        {/* Delete errors + invite confirmation surface above the card. */}
        {(deleteUser.isError || (createUser.isSuccess && invitedEmail)) && (
          <div className="flex flex-col gap-2 mb-4">
            {deleteUser.isError && (
              <ErrorBanner theme={theme} message={formatApiError(deleteUser.error)} />
            )}
            {createUser.isSuccess && invitedEmail && (
              <SuccessBanner theme={theme} message={`Invite sent to ${invitedEmail}.`} />
            )}
          </div>
        )}

        <SettingsCard
          icon={<Building2 size={17} strokeWidth={1.75} />}
          title="Company Settings"
          subtitle={`Manage who has access to ${user.client.name}`}
          action={
            <button type="button" onClick={startAdd} className="btn" style={primaryButton(theme)}>
              <UserPlus size={14} /> Add user
            </button>
          }
        >
          {/* Users table */}
          <div className="overflow-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr
                  style={{
                    backgroundColor: theme.bgSurface2,
                    borderBottom: `1px solid ${theme.borderColor}`,
                  }}
                >
                  <th className="px-4 py-2.5 text-left" style={thStyle(theme)}>
                    User
                  </th>
                  <th className="px-4 py-2.5 text-left" style={thStyle(theme)}>
                    Email
                  </th>
                  <th className="px-4 py-2.5 text-left" style={thStyle(theme)}>
                    Role
                  </th>
                  <th className="px-4 py-2.5 text-right" style={thStyle(theme)}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6" style={emptyCellStyle(theme)}>
                      Loading…
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center" style={emptyCellStyle(theme)}>
                      No users yet.
                    </td>
                  </tr>
                ) : (
                  list.map((u) => (
                    <tr
                      key={u.id}
                      style={{ borderBottom: `1px solid ${theme.borderColor}` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.bgSurface2
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent"
                      }}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={28} />
                          <span
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: theme.textPrimary,
                            }}
                          >
                            {u.name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5" style={cellStyle(theme)}>
                        {u.email_address}
                      </td>
                      <td className="px-4 py-2.5" style={cellStyle(theme)}>
                        {roleLabel(u.role)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(u)}
                            className="btn"
                            style={iconButton(theme)}
                            title="Edit"
                          >
                            <Pencil size={13} /> Edit
                          </button>
                          {u.id !== user.id && (
                            <button
                              type="button"
                              onClick={() => handleRemove(u)}
                              disabled={deleteUser.isPending}
                              className="btn"
                              style={{ ...iconButton(theme), color: theme.brandAccent }}
                              title="Remove"
                            >
                              <Trash2 size={13} /> Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </SettingsCard>

        {/* Add / edit user modal */}
        {showForm && (
          <Modal title={editing ? "Edit user" : "Add user"} onClose={resetForm} maxWidth={520}>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  <label className="flex flex-col gap-1.5">
                    <span style={labelStyle(theme)}>Email</span>
                    {editing ? (
                      <input
                        type="text"
                        value={form.email}
                        disabled
                        style={{
                          ...inputStyle(theme),
                          backgroundColor: theme.bgSurface2,
                          color: theme.textMuted,
                          cursor: "not-allowed",
                        }}
                      />
                    ) : (
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="name@example.com"
                        style={inputStyle(theme)}
                      />
                    )}
                  </label>

                  <label className="flex flex-col gap-1.5">
                    <span style={labelStyle(theme)}>Name</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      style={inputStyle(theme)}
                    />
                  </label>

                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <span style={labelStyle(theme)}>Role</span>
                    <SelectMenu
                      value={form.role}
                      onChange={(v) => setForm((f) => ({ ...f, role: v as Role }))}
                      options={ROLE_OPTIONS}
                      width="100%"
                      background={theme.bgSurface}
                      theme={theme}
                    />
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "12px",
                        color: theme.textDim,
                        marginTop: "2px",
                      }}
                    >
                      {ROLE_CAPTIONS[form.role]}
                    </span>
                  </div>
                </div>

                {formError && <ErrorBanner theme={theme} message={formError} />}
              </div>

              <div className="px-6 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn w-full sm:w-auto"
                  style={{ ...secondaryButton(theme), justifyContent: "center" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSaving}
                  className="btn w-full sm:w-auto"
                  style={{
                    ...primaryButton(theme),
                    justifyContent: "center",
                    opacity: formSaving ? 0.6 : 1,
                  }}
                >
                  {formSaving ? "Saving…" : editing ? "Save changes" : "Send invite"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  )
}

function thStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "11px",
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  }
}

function cellStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    color: theme.textMuted,
  }
}

function emptyCellStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    color: theme.textMuted,
  }
}

function iconButton(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 9px",
    borderRadius: "7px",
    backgroundColor: theme.bgSurface2,
    color: theme.textMuted,
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}
